import React, { useEffect, useId, useRef } from 'react';
import { X } from 'lucide-react';
import { Button } from './Button';
import { useLocale } from '../../contexts/LocaleContext';
import { focusFirst, trapTabKey } from '../../utils/focusTrap';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  returnFocusRef?: React.RefObject<HTMLElement>;
  dismissible?: boolean;
}

const sizeClasses = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-2xl',
};

export function Modal({
  open,
  onClose,
  title,
  children,
  size = 'md',
  returnFocusRef,
  dismissible = true,
}: ModalProps) {
  const { t } = useLocale();
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  const dismissibleRef = useRef(dismissible);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const titleId = `modal-title-${useId().replace(/:/g, '')}`;

  useEffect(() => {
    closeRef.current = onClose;
    dismissibleRef.current = dismissible;
  }, [onClose, dismissible]);

  useEffect(() => {
    if (!open) return;
    previousFocusRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const focusFrame = window.requestAnimationFrame(() => focusFirst(dialogRef.current));
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        if (dismissibleRef.current) closeRef.current();
        return;
      }
      trapTabKey(event, dialogRef.current);
    };
    document.addEventListener('keydown', handler);
    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.removeEventListener('keydown', handler);
      document.body.style.overflow = previousOverflow;
      const previousFocus = previousFocusRef.current;
      window.requestAnimationFrame(() => {
        const previousFocusUsable =
          previousFocus?.isConnected &&
          previousFocus !== document.body &&
          previousFocus !== document.documentElement;
        if (previousFocusUsable) previousFocus.focus();
        else if (returnFocusRef?.current?.isConnected) returnFocusRef.current.focus();
      });
    };
  }, [open, returnFocusRef]);

  if (!open) return null;

  return (
    // m-0: pages render dialogs inside space-y-* stacks, whose sibling margin would otherwise
    // offset this fixed overlay and leave a strip of the page uncovered.
    <div className="fixed inset-0 z-50 m-0 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-xs"
        aria-hidden="true"
        onClick={dismissible ? onClose : undefined}
      />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={`relative w-full ${sizeClasses[size]} max-h-[calc(100dvh-2rem)] overflow-y-auto overscroll-contain rounded-xl border border-slate-700 bg-slate-900 shadow-2xl`}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700">
          <h2 id={titleId} className="text-base font-semibold text-slate-100">
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={!dismissible}
            aria-label={t('closeDialog')}
            title={t('closeDialog')}
            className="text-slate-400 hover:text-slate-100 transition-colors p-2 rounded-md hover:bg-slate-800 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <X size={16} aria-hidden="true" />
          </button>
        </div>
        <div className="px-6 py-5">{children}</div>
      </div>
    </div>
  );
}

interface ConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmLabel?: string;
  confirmVariant?: 'primary' | 'danger';
  loading?: boolean;
}

export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel,
  confirmVariant = 'danger',
  loading = false,
}: ConfirmDialogProps) {
  const { t } = useLocale();
  return (
    <Modal open={open} onClose={onClose} title={title} size="sm" dismissible={!loading}>
      <p className="text-sm text-slate-300 mb-6">{message}</p>
      <div className="flex justify-end gap-3">
        <Button variant="secondary" size="sm" onClick={onClose} disabled={loading}>
          {t('cancel')}
        </Button>
        <Button variant={confirmVariant} size="sm" onClick={onConfirm} loading={loading}>
          {confirmLabel ?? t('confirm')}
        </Button>
      </div>
    </Modal>
  );
}
